import { Component } from '@angular/core';
import { ActivatedRoute, NavigationStart, Route, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { StatusType } from '@uigy/generated/graphql';
import { LicenseManagerService } from '@uigy/modules/license/license-manager.service';
import { LicenseNotificationsService } from '@uigy/modules/license/license-notifications.service';
import { routes } from '@uigy/modules/mini-layout/mini-layout-routing.module';
import { RoutesStatesNamesEnum } from '@uigy/shared/enums/routesStatesNames.enum';
import { AppInitializationService } from '@uigy/shared/services/app-initialization.service';
import { FlagsService } from '@uigy/shared/services/flags.service';
import { IemService } from '@uigy/shared/services/iem.service';
import { State } from '@uigy/store/appState';
import { getStatus } from '@uigy/store/selectors/auth.selectors';
import { combineLatest, Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { LoadingService } from '@uigy/shared/services/loading.service';
import { ConfigService } from '@uigy/shared/services/config.service';
import Utils from '@uigy/utils';
import { GridExportService } from '@uigy/shared/modules/grid/grids/services/grid-export.service';
import { GridReadyEvent } from '@ag-grid-community/core';
import { DefaultPathService } from '@uigy/shared/services/default-path.service';
import { PendoService } from '@uigy/shared/services/pendo.service';
import { LoginOnboardingService } from '@uigy/shared/services/login-onboarding.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent {
  private static readonly NOT_AUTHENTICATED_ROUTES: string[] = routes.map((route: Route) => '/' + route.path).concat('/');
  public appInitialized$: Observable<boolean> = this.appInitializationService.appInitialized.pipe(
    tap((isInitialized: boolean) => {
      if (isInitialized) {
        this.initStatus();

        this.iemService.machineId = this.activatedRoute.snapshot.paramMap.get('id');

        this.licenseNotificationsService.start();

        this.router.events.pipe(
          filter(event => event instanceof NavigationStart),
          tap(() => {
            // reset on start of each navigation
            this.loadingService.reset();
          }),
        ).subscribe();
      } else {
        console.error('app initialization failed');
      }
    }),
  );

  constructor(
      private appInitializationService: AppInitializationService,
      private router: Router,
      private store: Store<State>,
      private licenseManagerService: LicenseManagerService,
      private licenseNotificationsService: LicenseNotificationsService,
      private iemService: IemService,
      private flagsService: FlagsService,
      private loadingService: LoadingService,
      private activatedRoute: ActivatedRoute,
      private pendoService: PendoService,
      private gridExportService: GridExportService,
      public configService: ConfigService,
      private defaultPathService: DefaultPathService,
      private loginOnboardingService: LoginOnboardingService,
  ) {
    // Expose Angular router to the global window object to route via pendo guide
    (window as any).ng = (window as any).ng || {};
    (window as any).ng.router = this.router;
  }

  public setExportGridAPI(params: GridReadyEvent): void {
    this.gridExportService.gridApi = params.api;
  }

  private initStatus(): void {
    const status$ = this.store.pipe(select(getStatus));
    const license$ = this.licenseManagerService.isLicenseInitialized()
      .pipe(tap((licenseInit) => {
        if (licenseInit) {
          this.flagsService.renewFlagsSubscription();
        }
      }));

    combineLatest([status$, license$]).subscribe(
      ([status, license]: [StatusType | null, boolean]) => {
        if (status === StatusType.Enabled && license) {
          this.pendoService.initialize();
        }
        this.navigateByStatus(status);
      },
    );
  }

  private async navigateByStatus(status: StatusType | null): Promise<any> {
    switch (status) {
      case StatusType.Uninitialized:
        return this.router.navigate(['/', RoutesStatesNamesEnum.startSetupWizard]);

      case StatusType.Disabled:
        return this.router.navigate(['/', RoutesStatesNamesEnum.enablePage]);

      case StatusType.Enabled: {
        this.loginOnboardingService.handleOnboardingPopup();
        // since the ICP view includes the UUID in the URL, we want to redirect the user to the dashboard if no path is specified
        if (AppComponent.NOT_AUTHENTICATED_ROUTES.includes(window.location.pathname) || (this.iemService.isIcpViewInEm() && this.isIcpInEmPathEmpty())) {
          return this.router.navigate(this.defaultPathService.getDefaultPathParts);
        }
      }
    }
  }

  /**
   * True if path to the IcpInEm only contains the UUID meaning pathLength = 1
   */
  private isIcpInEmPathEmpty(): boolean {
    return Utils.getPathLength(window.location.pathname) == 1;
  }
}
